/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
    NavCategory,
    NavCategoryItem,
    NavDivider,
    NavDrawer,
    NavDrawerBody,
    NavItem,
    NavSubItem,
    NavSubItemGroup,
} from "@fluentui/react-nav-preview";
import {
    CalendarRtl20Regular,
    ClipboardCheckmark20Regular,
    Notepad20Regular,
    PersonQuestionMark20Regular,
    PeopleTeam20Regular,
    Chat20Regular,
    Alert20Regular,
    BookOpen20Regular,
} from "@fluentui/react-icons";
import { Tooltip, makeStyles, tokens } from "@fluentui/react-components";
import { Link, useLocation } from "react-router-dom";
import ShiftsContext from '../Context/ShiftsContext'
import { useDispatch, useSelector } from 'react-redux';
import { profilePage } from '../actions'


const navContainer = () => css`
        .nav-items-container{
            button.fui-NavItem{
                font-size:15px;
            }
        }
`

const useStyles = makeStyles({
    root: {
        overflow: "hidden",
        display: "flex",
        height: "100%",
        paddingTop: "10px",
        backgroundColor: "var(--colorNeutralBackground4)",
    },
    content: {
        flex: "1",
        padding: "16px",
        display: "grid",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    field: {
        display: "flex",
        marginTop: "4px",
        marginLeft: "8px",
        flexDirection: "column",
        gridRowGap: tokens.spacingVerticalS,
    },
    link: {
        textDecoration: "none",
        fontSize:15,
        color: "inherit", // Ensure links inherit text and icon colors
    },
});

export const EmployeeNav = () => {
    const styles = useStyles();
    const location = useLocation(); // Hook to get the current URL
    const [isMultiple, setIsMultiple] = React.useState(true);
    const [navDrawerType, setNavDrawerType] = React.useState("inline");
    const { navOpen, setNavOpen } = useContext(ShiftsContext);
    const [mobile, setMobile] = useState(window.innerWidth <= 1024);

    const dispatch = useDispatch()

    const { auditsModule, formModule, scheduleModule, timeOffModule } = useSelector(state => ({
        formModule: state.appState.clientSettings.formsModule,
        auditsModule: state.appState.clientSettings.auditsModule,
        scheduleModule: state.appState.clientSettings.scheduleModule,
        timeOffModule: state.appState.clientSettings.timeOffModule
    }));

    // Function to update NavDrawer type based on window size
    const updateNavDrawerType = () => {
        const isMobileOrTablet = window.matchMedia("(max-width: 1024px)").matches;
        setNavDrawerType(isMobileOrTablet ? "overlay" : "inline");
    };

    const handleResize = () => {
        setMobile(window.innerWidth <= 1024);
    };

    React.useEffect(() => {
        handleResize()
        // Set initial type based on current window size
        updateNavDrawerType();

        // Add resize event listener
        window.addEventListener("resize", updateNavDrawerType);
        window.addEventListener('resize', handleResize);
        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener("resize", updateNavDrawerType);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Map URL paths to NavItem values
    const navItemValues = {
        "/scheduler/my-schedule": "1",
        "/admin/overview/audits": "2",
        "/user/forms": "3",
        "/schedule/requests": "5",
        "/schedule/requests/swap-requests": "6",
        "/schedule/requests/availability": "7",
        "/scheduler/colleagues": "8",
        "/chats": "9",
        "/notifications-alerts": "13",
        "/help/contact-support": "11",
        "/help/suggest-feature": "12",
        "/quizzes": "14",
    };


    // Get the selected value based on the current path
    const selectedValue = navItemValues[location.pathname] || "";
    const ref = React.useRef(null);

    const closeNav = () => {
        if (!mobile) {
            return;
        }
        setNavOpen(false)
    }

    const handleClickOpen = (page) => {
        dispatch(profilePage(page))
        closeNav()
    };

    return (
        <div className={styles.root} ref={ref} css={ navContainer }>
            <NavDrawer
                selectedValue={selectedValue} // Set selectedValue dynamically
                open={navOpen}
                type={navDrawerType} // Dynamic type based on screen size
                multiple={isMultiple}
                mountNode={ref.current}
            >
                <NavDrawerBody className="nav-items-container">
                    {scheduleModule && <Link to="/scheduler/my-schedule" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<CalendarRtl20Regular />} value="1">
                            Schedule
                        </NavItem>
                    </Link>}
                    {auditsModule && <Link to="/admin/overview/audits" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<ClipboardCheckmark20Regular />} value="2">
                            Audits
                        </NavItem>
                    </Link>}
                    {formModule && <Link to="/user/forms" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<Notepad20Regular />} value="3">
                            Forms
                        </NavItem>
                    </Link>}
                    <Link to="/quizzes" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<BookOpen20Regular />} value="14">
                            Quizzes & Lessons
                        </NavItem>
                    </Link>
                    <NavCategory value="4">
                        <NavCategoryItem icon={<PersonQuestionMark20Regular />}>
                            Requests
                        </NavCategoryItem>
                        <NavSubItemGroup>
                            {timeOffModule && <Link to="/schedule/requests" onClick={closeNav} className={styles.link}>
                                <NavSubItem value="5">Request Off</NavSubItem>
                            </Link>}
                            {scheduleModule && <Link to="/schedule/requests/swap-requests" onClick={closeNav} className={styles.link}>
                                <NavSubItem value="6">Swap Requests</NavSubItem>
                            </Link>}
                            <Link to="/schedule/requests/availability" onClick={closeNav} className={styles.link}>
                                <NavSubItem value="7">Availability</NavSubItem>
                            </Link>
                        </NavSubItemGroup>
                    </NavCategory>
                    <Link to="/scheduler/colleagues" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<PeopleTeam20Regular />} value="8">
                            Colleagues
                        </NavItem>
                    </Link>
                    <Link to="/chats" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<Chat20Regular />} value="9">
                            Messages
                        </NavItem>
                    </Link>
                    <Link to="/notifications-alerts" onClick={closeNav} className={styles.link}>
                        <NavItem icon={<Alert20Regular />} value="13">
                            Notifications & Alerts
                        </NavItem>
                    </Link>
                    <NavDivider />
                    <NavCategory value="10">
                        <NavCategoryItem icon={<PersonQuestionMark20Regular />}>
                            Help
                        </NavCategoryItem>
                        <NavSubItemGroup>
                            <NavSubItem value="11" onClick={() => handleClickOpen(6)}>Contact Support</NavSubItem>
                            <NavSubItem value="12" onClick={() => handleClickOpen(7)}>Suggest a Feature</NavSubItem>
                        </NavSubItemGroup>
                    </NavCategory>
                </NavDrawerBody>
            </NavDrawer>
        </div>
    );
};
